import React, { useState, useEffect } from 'react';

function SubmitForm({ language }) {
  const [name, setName] = useState('');
  const [quote, setQuote] = useState('');
  const [proof, setProof] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const translations = {
    ar: {
      title: 'إضافة حكمة جديدة',
      nameLabel: 'صاحب الحكمة',
      namePlaceholder: 'أدخل اسم صاحب الحكمة',
      quoteLabel: 'الحكمة',
      quotePlaceholder: 'أدخل نص الحكمة هنا',
      proofLabel: 'السند والصحة',
      proofPlaceholder: 'أدخل السند أو دليل الصحة (إن وجد)',
      submitButton: 'إرسال',
      successMessage: 'شكراً لمساهمتك! سيتم مراجعة الحكمة وإضافتها قريباً.',
      errorMessage: 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.',
      sendingMessage: 'جاري الإرسال...'
    },
    en: {
      title: 'Add New Wisdom',
      nameLabel: 'Author of the Wisdom',
      namePlaceholder: 'Enter the name of the author',
      quoteLabel: 'Wisdom',
      quotePlaceholder: 'Enter here',
      proofLabel: 'Proof or Authenticity',
      proofPlaceholder: 'Enter the proof or authenticity (if available)',
      submitButton: 'Submit',
      successMessage: 'Thank you for your contribution! The request will be reviewed and added soon.',
      errorMessage: 'An error occurred while submitting. Please try again.',
      sendingMessage: 'Sending...'
    },
    fr: {
      title: 'Ajouter une nouvelle sagesse',
      nameLabel: 'Auteur de la sagesse',
      namePlaceholder: 'Entrez le nom de l\'auteur',
      quoteLabel: 'Sagesse',
      quotePlaceholder: 'Saisissez la sagesse ici',
      proofLabel: 'Preuve ou authenticité',
      proofPlaceholder: 'Entrez la preuve ou l\'authenticité (si disponible)',
      submitButton: 'Envoyer',
      successMessage: 'Merci pour votre contribution ! La demande sera examinée et ajoutée prochainement.',
      errorMessage: 'Une erreur est survenue lors de l\'envoi. Veuillez réessayer.',
      sendingMessage: 'Envoi en cours...'
    }
  };

  const t = translations[language] || translations.ar;

  const formDirection = ['en', 'fr'].includes(language) ? 'ltr' : 'rtl';
  const textAlign = ['en', 'fr'].includes(language) ? 'left' : 'right';

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setError(null);

  try {
    const response = await fetch(`${process.env.REACT_APP_WORKER_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_API_KEY}`
      },
      body: JSON.stringify({
        name,
        quote,
        proof: proof || 'غير متوفر' // valeur par défaut si proof vide
      })
    });

    if (!response.ok) {
      throw new Error(t.errorMessage);
    }

    setName('');
    setQuote('');
    setProof('');
    setShowSuccess(true); // Only set showSuccess here
  } catch (err) {
    setError(err.message || t.errorMessage);
  } finally {
    setIsLoading(false);
  }
};

  // Fermeture automatique du message de succès après 5 secondes
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Fermer le message de succès quand clic en dehors de la boîte
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowSuccess(false);
    }
  };

  return (
    <div className="form-container">
      <form
        onSubmit={handleSubmit}
        className="submit-form"
        style={{ direction: formDirection }}
      >
        <h2 className="form-title" style={{ textAlign }}>
          {t.title}
        </h2>

        <div className="form-group">
          <label htmlFor="name" style={{ textAlign }}>
            {t.nameLabel}
          </label>
          <input
            id="name"
            type="text"
            placeholder={t.namePlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="form-textarea"
            style={{ direction: formDirection, textAlign }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="quote" style={{ textAlign }}>
            {t.quoteLabel}
          </label>
          <textarea
            id="quote"
            placeholder={t.quotePlaceholder}
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            required
            className="form-textarea"
            rows="4"
            style={{ direction: formDirection, textAlign }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="proof" style={{ textAlign }}>
            {t.proofLabel}
          </label>
          <input
            id="proof"
            type="text"
            placeholder={t.proofPlaceholder}
            value={proof}
            onChange={(e) => setProof(e.target.value)}
            className="form-textarea"
            style={{ direction: formDirection, textAlign }}
          />
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={isLoading}
        >
          {isLoading ? t.sendingMessage : t.submitButton}
        </button>
      </form>

      {error && (
        <div
          className="error-message"
          style={{ direction: formDirection, textAlign, color: 'red' }}
        >
          {error}
        </div>
      )}

      {showSuccess && (
        <div
          className="success-overlay"
          onClick={handleOverlayClick}
          style={{ direction: formDirection }}
          aria-live="polite"
          role="alert"
        >
          <div
            className="success-message-fixed"
            onClick={(e) => e.stopPropagation()}
            style={{ direction: formDirection }}
          >
            <div className="success-message-wrapper">
              <button
                className="close-button"
                onClick={() => setShowSuccess(false)}
                aria-label={language === 'ar' ? 'إغلاق' : language === 'fr' ? 'Fermer' : 'Close'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    fill="#f8f3e9"
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </svg>
              </button>

              <div className="success-message-box">
                <svg
                  className="success-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#f8f3e9"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </svg>
                <p className="success-message-text">{t.successMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubmitForm;
